import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Grid, Card, CardContent, Typography, Dialog, DialogTitle, 
  DialogContent, DialogActions, Button, TextField, Tabs, Tab, styled, Select, MenuItem, 
  FormControl, InputLabel 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { QRCodeCanvas } from 'qrcode.react';
import { collection, getDocs, updateDoc, deleteDoc, doc, setDoc, query, where, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { arrayUnion, arrayRemove } from 'firebase/firestore';

const StyledCard = styled(Card)(({ theme }) => ({
  minWidth: 150,
  height: 150,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[8],
  },
}));

const AddCard = styled(Card)(({ theme }) => ({
  minWidth: 150,
  height: 150,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  cursor: 'pointer',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[8],
  },
}));

const ManageTable = ({ companyCode, companyType }) => {
  const [tables, setTables] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedUnitCode, setSelectedUnitCode] = useState('all');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [newTable, setNewTable] = useState({ id: '', qrCode: '', /*assignedServer: '',*/ unitCode: '' });
  const [selectedTable, setSelectedTable] = useState(null);
  const [editTable, setEditTable] = useState({ id: '', qrCode: '', occupied: false, currentOrderId: '', /*assignedServer: '',*/ unitCode: '',  tableStatus: 'Available' });
  const [customerDetails, setCustomerDetails] = useState({ name: '', mobile: '' });
  const [servers, setServers] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const addQrCodeRef = useRef(null);
  const editQrCodeRef = useRef(null);

  const getItemTypeAndIdField = (unitCode) => {
    if (unitCode === 'all' || !unitCode) {
      return { itemType: 'Table', idField: 'tableId' };
    }
    const selectedUnit = units.find(unit => unit.unitCode === unitCode);
    if (!selectedUnit || !selectedUnit.unitType) {
      return { itemType: 'Table', idField: 'tableId' };
    }
    const unitType = selectedUnit.unitType?.toLowerCase();
    return {
      itemType: unitType === 'restaurant' ? 'Table' : unitType === 'hotel' ? 'Room' : 'Table',
      idField: unitType === 'restaurant' ? 'tableId' : unitType === 'hotel' ? 'roomId' : 'tableId',
    };
  };

  const { itemType, idField } = getItemTypeAndIdField(selectedUnitCode);
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://platterly.infapp.biz';

  /*const initializeUnitTablesArray = async (unitCode) => {
    try {
      const unitDocRef = doc(db, 'units', unitCode);
      const unitDoc = await getDoc(unitDocRef);
      if (unitDoc.exists() && !Array.isArray(unitDoc.data().tables)) {
        await updateDoc(unitDocRef, { tables: [] });
        console.log(`ManageTable: Initialized empty tables array for unit ${unitCode}`);
      }
    } catch (error) {
      console.error(`ManageTable: Error initializing tables array for unit ${unitCode}:`, error);
    }
  };*/

  const [hasTransactions, setHasTransactions] = useState(false);
  const fetchServers = async () => {
    if (!companyCode) {
      console.error('ManageTable: No companyCode for fetching servers');
      return [];
    }
    try {
      let serversQuery;
      if (selectedUnitCode === 'all') {
        serversQuery = query(
          collection(db, 'users'),
          where('companyCode', '==', companyCode),
          where('role', '==', 'servers')
        );
      } else {
        serversQuery = query(
          collection(db, 'users'),
          where('companyCode', '==', companyCode),
          where('role', '==', 'servers'),
          where('unitCode', '==', selectedUnitCode)
        );
      }
      const serversSnapshot = await getDocs(serversQuery);
      const serverList = serversSnapshot.docs.map(doc => ({
        userId: doc.data().userId || doc.id,
        name: doc.data().name || doc.data().userId || doc.id
      }));
      console.log('ManageTable: Fetched servers:', serverList);
      return serverList;
    } catch (error) {
      console.error('ManageTable: Error fetching servers:', error);
      return [];
    }
  };

  const fetchUnits = async () => {
    if (!companyCode) {
      console.error('ManageTable: No companyCode for fetching units');
      return [];
    }
    try {
      const unitsQuery = query(collection(db, 'units'), where('companyCode', '==', companyCode));
      const unitsSnapshot = await getDocs(unitsQuery);
      const unitList = unitsSnapshot.docs.map(doc => ({
        unitCode: doc.id,
        name: doc.data().unitName || doc.id, // Use unitName instead of name
        unitType: doc.data().unitType || 'Unknown'
      }));
      console.log('ManageTable: Fetched units:', unitList);
      // Initialize tables array for each unit
      //await Promise.all(unitList.map(unit => initializeUnitTablesArray(unit.unitCode)));
      return unitList;
    } catch (error) {
      console.error('ManageTable: Error fetching units:', error);
      return [];
    }
  };

  useEffect(() => {
    if (!companyCode) {
      console.error('ManageTable: No companyCode provided');
      return;
    }

    fetchUnits().then(unitList => {
      setUnits(unitList);
      if (unitList.length === 0) {
        console.warn('ManageTable: No units found for companyCode:', companyCode);
      }
    });

    fetchServers().then(serverList => {
      setServers(serverList);
      if (serverList.length === 0) {
        console.warn('ManageTable: No servers found for companyCode:', companyCode);
      }
    });

    const tablesQuery = selectedUnitCode === 'all'
      ? query(collection(db, 'tables'), where('companyCode', '==', companyCode))
      : query(collection(db, 'tables'), where('companyCode', '==', companyCode), where('unitCode', '==', selectedUnitCode));

    const unsubscribeTables = onSnapshot(tablesQuery, (querySnapshot) => {
      const tableList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTables(tableList);
      console.log(`ManageTable: Real-time update - fetched ${tableList.length} ${itemType.toLowerCase()}s for companyCode: ${companyCode}, unitCode: ${selectedUnitCode}`);
    }, (error) => {
      console.error(`ManageTable: Error fetching ${itemType.toLowerCase()}s in real-time:`, error);
    });

    return () => {
      unsubscribeTables();
      console.log('ManageTable: Cleaned up Firestore listener');
    };
  }, [companyCode, selectedUnitCode, itemType, idField]);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (itemType === 'Customer' && selectedTable?.currentOrderId) {
        try {
          const orderDocRef = doc(db, 'orders', selectedTable.currentOrderId);
          const orderDoc = await getDoc(orderDocRef);
          if (orderDoc.exists()) {
            const orderData = orderDoc.data();
            setCustomerDetails({
              name: orderData.customerName || 'N/A',
              mobile: orderData.customerMobile || 'N/A',
            });
            console.log(`ManageTable: Fetched customer details for order ${selectedTable.currentOrderId}:`, {
              name: orderData.customerName,
              mobile: orderData.customerMobile,
            });
          } else {
            setCustomerDetails({ name: 'N/A', mobile: 'N/A' });
            console.log(`ManageTable: No order found for orderId: ${selectedTable.currentOrderId}`);
          }
        } catch (error) {
          console.error(`ManageTable: Error fetching customer details for order ${selectedTable.currentOrderId}:`, error);
          setCustomerDetails({ name: 'N/A', mobile: 'N/A' });
        }
      }
    };
    fetchCustomerDetails();
  }, [itemType, selectedTable]);

  /*const generateQrCodeUrl = (id, unitCode) => {
    if (!id || !unitCode) return '';
    const sanitizedId = id.replace(/\s+/g, '_');
    const selectedUnit = units.find(unit => unit.unitCode === unitCode);
    const unitType = selectedUnit ? selectedUnit.unitType : 'Unknown';
    return `${baseUrl}/customer/home?code=${companyCode}&unit=${unitCode}&id=${sanitizedId}&type=${unitType}`;
  };*/
  /*const generateQrCodeUrl = (id, unitCode) => {
  if (!id || !unitCode) return '';

  const sanitizedId = id.replace(/\s+/g, '_');
  const selectedUnit = units.find(unit => unit.unitCode === unitCode);
  const unitType = selectedUnit ? selectedUnit.unitType : 'Unknown';

  return `${baseUrl}/customer/details?code=${companyCode}&unit=${unitCode}&id=${sanitizedId}&type=${unitType}`;
};*/
const generateQrCodeUrl = (id, unitCode, serviceArea) => {
  if (!id || !unitCode) return '';

  const sanitizedId = id.replace(/\s+/g, '_');
  const selectedUnit = units.find(unit => unit.unitCode === unitCode);
  const unitType = selectedUnit ? selectedUnit.unitType : 'Unknown';

  return `${baseUrl}/customer/details?code=${companyCode}&unit=${unitCode}&id=${sanitizedId}&type=${unitType}&serviceArea=${serviceArea || ''}`;
};
  const handleGenerateQrCode = (id, isEdit = false) => {
     const serviceArea = isEdit ? editTable.serviceArea : newTable.serviceArea;
    /*const qrCodeUrl = generateQrCodeUrl(id, isEdit ? editTable.unitCode : newTable.unitCode);*/
     const qrCodeUrl = generateQrCodeUrl(
    id,
    isEdit ? editTable.unitCode : newTable.unitCode,
    serviceArea
  );
    if (isEdit) {
      setEditTable(prev => ({ ...prev, qrCode: qrCodeUrl }));
    } else {
      setNewTable(prev => ({ ...prev, qrCode: qrCodeUrl }));
    }
    console.log(`ManageTable: Generated QR code for ${itemType.toLowerCase()} ID: ${id}, QR: ${qrCodeUrl}`);
  };

  const handleDownloadQrCode = (id, isEdit = false) => {
    const canvas = isEdit ? editQrCodeRef.current : addQrCodeRef.current;
    if (canvas) {
      const qrCodeUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = qrCodeUrl;
      link.download = `qrcode_${companyCode}_${id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log(`ManageTable: Downloaded QR code for ${itemType.toLowerCase()} ID: ${id}`);
    } else {
      console.error('ManageTable: QR code canvas not found');
    }
  };

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    setNewTable({ id: '', qrCode: '', /*assignedServer: '',*/ unitCode: selectedUnitCode === 'all' ? '' : selectedUnitCode });
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleOpenEditDialog = async (table) => {
    setSelectedTable(table);
    setEditTable({ 
      id: table[idField], 
      qrCode: table.qrCode, 
      occupied: table.occupied === true || table.occupied === false ? table.occupied : false,
      currentOrderId: table.currentOrderId || '',
      //assignedServer: table.assignedServer || '',
      unitCode: table.unitCode || '',
      serviceArea: table.serviceArea || '',
      tableStatus: table.tableStatus || "Available"
    });
    setOpenEditDialog(true);

 try {
    let hasActive = false;

    // 1. Check currentOrderId directly (fast path)
    if (table.currentOrderId) {
      const orderRef = doc(db, "orders", table.currentOrderId);
      const orderSnap = await getDoc(orderRef);

      if (orderSnap.exists()) {
        const orderData = orderSnap.data();

        // define what "active" means in your system
        if (orderData.orderStatus !== "Paid" && orderData.orderStatus !== "Cancelled") {
          hasActive = true;
        }
      }
    }

    // 2. Fallback: query active orders
    if (!hasActive) {
      const ordersQuery = query(
        collection(db, "orders"),
        where(idField, "==", table[idField]),
        where("unitCode", "==", table.unitCode),
        where("orderStatus", "==", "Open")
      );

      const orderSnapshot = await getDocs(ordersQuery);

      if (!orderSnapshot.empty) {
        hasActive = true;
      }
    }

    //  3. Check active payments
    if (!hasActive) {
      const paymentsQuery = query(
        collection(db, "payments"),
        where(idField, "==", table[idField]),
        where("unitCode", "==", table.unitCode),
        where("paymentStatus", "!=", "Success")
      );

      const paymentSnapshot = await getDocs(paymentsQuery);

      if (!paymentSnapshot.empty) {
        hasActive = true;
      }
    }

    setHasTransactions(hasActive);

  } catch (error) {
    console.error("Error checking active transactions:", error);
    setHasTransactions(false);
  }
  
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedTable(null);
    setCustomerDetails({ name: '', mobile: '' });
  };

  const handleNewTableChange = (e) => {
    const { name, value } = e.target;
    setNewTable(prev => ({ ...prev, [name]: value }));
  };

  /*const handleEditTableChange = (e) => {
    const { name, value } = e.target;
    setEditTable(prev => ({ 
      ...prev, 
      [name]: name === 'occupied' ? value === 'true' : value 
    }));
  };*/
const handleEditTableChange = (e) => {
  const { name, value } = e.target;
  if (name === 'occupied') {
    setEditTable(prev => ({ ...prev, occupied: value === 'true' }));
  } else if (name === 'unitCode') {
    setEditTable(prev => ({ ...prev, unitCode: value, serviceArea: '' }));
  } else if (name === 'serviceArea') {
    setEditTable(prev => ({ ...prev, serviceArea: value }));
  } else {
    setEditTable(prev => ({ ...prev, [name]: value }));
  }
};

  const handleAddTable = async () => {
    if (!newTable.id || !newTable.qrCode || !newTable.unitCode) {
      alert(`Please enter a ${itemType.toLowerCase()} ID, generate a QR code, and select a unit`);
      return;
    }
    try {
      const sanitizedTableId = newTable.id.replace(/\s+/g, '_');
      const docId = `${companyCode}_${newTable.unitCode}_${sanitizedTableId}`;
      const tableRef = doc(db, 'tables', docId);
      const tableDoc = await getDoc(tableRef);
      if (tableDoc.exists()) {
        alert(`A ${itemType.toLowerCase()} with ID ${sanitizedTableId} already exists for this unit.`);
        return;
      }
      const companyDocRef = doc(db, 'companies', companyCode);
      const companyDoc = await getDoc(companyDocRef);
      const companyName = companyDoc.exists() ? companyDoc.data().name : 'Unknown Company';
      const unitDocRef = doc(db, 'units', newTable.unitCode);
      const unitDoc = await getDoc(unitDocRef);
      if (!unitDoc.exists()) {
        alert(`Unit ${newTable.unitCode} does not exist.`);
        return;
      }
      const unitData = unitDoc.data();
      const unitName = unitData.unitName || 'Unknown Unit'; // Use unitName
      /*await initializeUnitTablesArray(newTable.unitCode);
      const unitTables = Array.isArray(unitData.tables) ? unitData.tables : [];
      if (unitTables.includes(sanitizedTableId)) {
        alert(`Table ID ${sanitizedTableId} already exists in unit ${newTable.unitCode}.`);
        return;
      }*/
      await setDoc(tableRef, {
        [idField]: sanitizedTableId,
        qrCode: newTable.qrCode,
        occupied: false,
        currentOrderId: '',
        //assignedServer: newTable.assignedServer || '',
        companyCode,
        companyName,
        unitCode: newTable.unitCode,
        unitName,
        serviceArea: newTable.serviceArea || '',
        tableStatus: "Available",
        activeSessions: [{ sessionId: Date.now().toString(), lastSeen: new Date() }]
      });
      
      /*await updateDoc(unitDocRef, {
        tables: arrayUnion(sanitizedTableId)
      });*/
      
      console.log(`ManageTable: Added ${itemType.toLowerCase()} with ID: ${docId}, QR: ${newTable.qrCode}, Assigned Server: ${newTable.assignedServer || 'None'}, Unit: ${newTable.unitCode}, Company Name: ${companyName}, Unit Name: ${unitName}`);
      console.log(`ManageTable: Updated unit ${newTable.unitCode} with table ${sanitizedTableId} in tables array`);
      handleCloseAddDialog();
    } catch (error) {
      console.error(`ManageTable: Error adding ${itemType.toLowerCase()}:`, error);
      alert(`Failed to add ${itemType.toLowerCase()}: ${error.message}`);
    }
  };

  /*const handleUpdateTable = async () => {
    if (!editTable.id || !editTable.qrCode || !editTable.unitCode) {
      alert(`Please enter a ${itemType.toLowerCase()} ID, generate a QR code, and select a unit`);
      return;
    }
    try {
      const sanitizedTableId = editTable.id.replace(/\s+/g, '_');
      const newDocId = `${companyCode}_${editTable.unitCode}_${sanitizedTableId}`;
      const oldDocId = selectedTable.id;
      const tableRef = doc(db, 'tables', newDocId);
      if (newDocId !== oldDocId) {
        const newTableDoc = await getDoc(tableRef);
        if (newTableDoc.exists()) {
          alert(`A ${itemType.toLowerCase()} with ID ${sanitizedTableId} already exists for unit ${editTable.unitCode}.`);
          return;
        }
      }
      const companyDocRef = doc(db, 'companies', companyCode);
      const companyDoc = await getDoc(companyDocRef);
      const companyName = companyDoc.exists() ? companyDoc.data().name : 'Unknown Company';
      const unitDocRef = doc(db, 'units', editTable.unitCode);
      const unitDoc = await getDoc(unitDocRef);
      if (!unitDoc.exists()) {
        alert(`Unit ${editTable.unitCode} does not exist.`);
        return;
      }
      const unitData = unitDoc.data();
      const unitName = unitData.unitName || 'Unknown Unit'; // Use unitName
      /*await initializeUnitTablesArray(editTable.unitCode);
      const unitTables = Array.isArray(unitData.tables) ? unitData.tables : [];
      if (unitTables.includes(sanitizedTableId) && newDocId !== oldDocId) {
        alert(`Table ID ${sanitizedTableId} already exists in unit ${editTable.unitCode}.`);
        return;
      }*/
     /* const oldUnitCode = selectedTable.unitCode;
      if (newDocId !== oldDocId) {
        const oldTableRef = doc(db, 'tables', oldDocId);
        await deleteDoc(oldTableRef);
        console.log(`ManageTable: Deleted old ${itemType.toLowerCase()} with ID: ${oldDocId}`);
        if (oldUnitCode && oldUnitCode !== editTable.unitCode) {
          const oldUnitDocRef = doc(db, 'units', oldUnitCode);
          /*await initializeUnitTablesArray(oldUnitCode);
          await updateDoc(oldUnitDocRef, {
            tables: arrayRemove(selectedTable[idField])
          });*/
          /*console.log(`ManageTable: Removed ${selectedTable[idField]} from unit ${oldUnitCode} tables array`);
        }
      }
      /*await updateDoc(unitDocRef, {
        tables: arrayUnion(sanitizedTableId)
      });*/
      /*await setDoc(tableRef, {
        [idField]: sanitizedTableId,
        qrCode: editTable.qrCode,
        occupied: editTable.occupied,
        currentOrderId: editTable.currentOrderId,
        //assignedServer: editTable.assignedServer || '',
        companyCode,
        companyName,
        unitCode: editTable.unitCode,
        unitName,
        serviceArea: editTable.serviceArea || '', // <-- add this
        tableStatus: editTable.tableStatus,
        activeSessions: [{ sessionId: Date.now().toString(), lastSeen: new Date() }]
      }, { merge: true });
      console.log('Updating table with data:', editTable);
      console.log(`ManageTable: Updated ${itemType.toLowerCase()} with ID: ${newDocId}, QR: ${editTable.qrCode}, Assigned Server: ${editTable.assignedServer || 'None'}, Unit: ${editTable.unitCode}, Company Name: ${companyName}, Unit Name: ${unitName}`);
      console.log(`ManageTable: Updated unit ${editTable.unitCode} with table ${sanitizedTableId} in tables array`);
      handleCloseEditDialog();
    } catch (error) {
      console.error(`ManageTable: Error updating ${itemType.toLowerCase()}:`, error);
      alert(`Failed to update ${itemType.toLowerCase()}: ${error.message}`);
    }
  };*/

  const handleUpdateTable = async () => {
  if (!editTable.id || !editTable.qrCode || !editTable.unitCode) {
    alert(`Please enter a ${itemType.toLowerCase()} ID, generate a QR code, and select a unit`);
    return;
  }

  try {
    // ADD IT HERE (VERY TOP INSIDE TRY)
    let safeEditTable = { ...editTable };

    if (hasTransactions) {
      safeEditTable.id = selectedTable[idField];
      safeEditTable.unitCode = selectedTable.unitCode;
      safeEditTable.serviceArea = selectedTable.serviceArea;
    }

    // use safeEditTable everywhere below
    const sanitizedTableId = safeEditTable.id.replace(/\s+/g, '_');
    const newDocId = `${companyCode}_${safeEditTable.unitCode}_${sanitizedTableId}`;
    const oldDocId = selectedTable.id;

    const tableRef = doc(db, 'tables', newDocId);

//ADD THIS BLOCK HERE
const oldTableId = selectedTable[idField];
const newTableId = sanitizedTableId;

if (newTableId !== oldTableId) {

  // UPDATE ORDERS
  const ordersQuery = query(
    collection(db, "orders"),
    where(idField, "==", oldTableId),
    where("unitCode", "==", selectedTable.unitCode)
  );

  const orderSnapshot = await getDocs(ordersQuery);

  const orderUpdates = orderSnapshot.docs.map(docSnap =>
    updateDoc(doc(db, "orders", docSnap.id), {
      [idField]: newTableId
    })
  );

  await Promise.all(orderUpdates);
  console.log("Orders updated");


  // UPDATE PAYMENTS
  const paymentsQuery = query(
    collection(db, "payments"),
    where(idField, "==", oldTableId),
    where("unitCode", "==", selectedTable.unitCode)
  );

  const paymentSnapshot = await getDocs(paymentsQuery);

  const paymentUpdates = paymentSnapshot.docs.map(docSnap =>
    updateDoc(doc(db, "payments", docSnap.id), {
      [idField]: newTableId
    })
  );

  await Promise.all(paymentUpdates);
  console.log("Payments updated");
}
    if (newDocId !== oldDocId) {
      const newTableDoc = await getDoc(tableRef);
      if (newTableDoc.exists()) {
        alert(`A ${itemType.toLowerCase()} with ID ${sanitizedTableId} already exists for unit ${safeEditTable.unitCode}.`);
        return;
      }
    }

    const companyDocRef = doc(db, 'companies', companyCode);
    const companyDoc = await getDoc(companyDocRef);
    const companyName = companyDoc.exists() ? companyDoc.data().name : 'Unknown Company';

    const unitDocRef = doc(db, 'units', safeEditTable.unitCode);
    const unitDoc = await getDoc(unitDocRef);

    if (!unitDoc.exists()) {
      alert(`Unit ${safeEditTable.unitCode} does not exist.`);
      return;
    }

    const unitData = unitDoc.data();
    const unitName = unitData.unitName || 'Unknown Unit';

    const oldUnitCode = selectedTable.unitCode;

    if (newDocId !== oldDocId) {
      const oldTableRef = doc(db, 'tables', oldDocId);
      await deleteDoc(oldTableRef);
      console.log(`ManageTable: Deleted old ${itemType.toLowerCase()} with ID: ${oldDocId}`);
      if (oldUnitCode && oldUnitCode !== safeEditTable.unitCode) {
         const oldUnitDocRef = doc(db, 'units', oldUnitCode);
        //console.log(`Removed ${selectedTable[idField]} from unit ${oldUnitCode}`);
        console.log(`ManageTable: Removed ${selectedTable[idField]} from unit ${oldUnitCode} tables array`);
      }
    }

    await setDoc(tableRef, {
      [idField]: sanitizedTableId,
      qrCode: safeEditTable.qrCode,
      occupied: safeEditTable.occupied,
      currentOrderId: safeEditTable.currentOrderId,
      companyCode,
      companyName,
      unitCode: safeEditTable.unitCode,
      unitName,
      serviceArea: safeEditTable.serviceArea || '',
      tableStatus: safeEditTable.tableStatus,
      activeSessions: [{ sessionId: Date.now().toString(), lastSeen: new Date() }]
    }, { merge: true });

    handleCloseEditDialog();

  } catch (error) {
    console.error(`ManageTable: Error updating ${itemType.toLowerCase()}:`, error);
    alert(`Failed to update ${itemType.toLowerCase()}: ${error.message}`);
  }
};
  const handleDeleteTable = async () => {
    try {
      const tableRef = doc(db, 'tables', selectedTable.id);
      const unitDocRef = doc(db, 'units', selectedTable.unitCode);
      const unitDoc = await getDoc(unitDocRef);
      if (!unitDoc.exists()) {
        alert(`Unit ${selectedTable.unitCode} does not exist.`);
        return;
      }
      //await initializeUnitTablesArray(selectedTable.unitCode);
      await deleteDoc(tableRef);
      /*await updateDoc(unitDocRef, {
        tables: arrayRemove(selectedTable[idField])
      });*/
      console.log(`ManageTable: Deleted ${itemType.toLowerCase()} with ID: ${selectedTable.id}`);
      console.log(`ManageTable: Removed ${selectedTable[idField]} from unit ${selectedTable.unitCode} tables array`);
      handleCloseEditDialog();
    } catch (error) {
      console.error(`ManageTable: Error deleting ${itemType.toLowerCase()}:`, error);
      alert(`Failed to delete ${itemType.toLowerCase()}: ${error.message}`);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleUnitChange = (event, newValue) => {
    setSelectedUnitCode(newValue);
    console.log(`ManageTable: Selected unit: ${newValue}`);
  };


  const [serviceAreas, setServiceAreas] = useState([]);
const [selectedServiceArea, setSelectedServiceArea] = useState('');

const [addServiceAreas, setAddServiceAreas] = useState([]);
const [editServiceAreas, setEditServiceAreas] = useState([]);

/*const fetchServiceAreas = async (unitCode) => {
  if (!unitCode || unitCode === 'all') return setServiceAreas([]);
  try {
    const serviceAreaQuery = query(
      collection(db, 'serviceAreas'),
      where('unitCode', '==', unitCode)
    );
    const snapshot = await getDocs(serviceAreaQuery);
    const areas = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
    setServiceAreas(areas);
  } catch (error) {
    console.error('ManageTable: Error fetching service areas:', error);
    setServiceAreas([]);
  }
};*/

const fetchServiceAreas = async (unitCode, setter) => {
  if (!unitCode || unitCode === "all") {
    setter([]);
    return;
  }

  try {
    const serviceAreaQuery = query(
      collection(db, "serviceAreas"),
      where("unitCode", "==", unitCode)
    );

    const snapshot = await getDocs(serviceAreaQuery);

    const areas = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    setter(areas);
  } catch (error) {
    console.error("ManageTable: Error fetching service areas:", error);
    setter([]);
  }
};

/*useEffect(() => {
  if (selectedUnitCode && selectedUnitCode !== 'all') {
    fetchServiceAreas(selectedUnitCode);
  } else {
    setServiceAreas([]);
  }
  // Reset selected service area when unit changes
  setSelectedServiceArea('');
}, [selectedUnitCode]);*/
/*useEffect(() => {
  const unitCodeToFetch = newTable.unitCode || selectedUnitCode;
  if (unitCodeToFetch && unitCodeToFetch !== 'all') {
    fetchServiceAreas(unitCodeToFetch);
  } else {
    setServiceAreas([]);
  }
  setSelectedServiceArea('');
}, [newTable.unitCode, selectedUnitCode]);*/
useEffect(() => {
  const unitCodeToFetch = newTable.unitCode || selectedUnitCode;

  if (unitCodeToFetch && unitCodeToFetch !== "all") {
    fetchServiceAreas(unitCodeToFetch, setAddServiceAreas);
  } else {
    setAddServiceAreas([]);
  }
}, [newTable.unitCode, selectedUnitCode]);
/*
useEffect(() => {
  const fetchEditServiceAreas = async () => {
    if (editTable.unitCode && editTable.unitCode !== 'all') {
      await fetchServiceAreas(editTable.unitCode);
    } else {
      setServiceAreas([]);
    }
  };
  fetchEditServiceAreas();
}, [editTable.unitCode]);*/
useEffect(() => {
  if (editTable.unitCode && editTable.unitCode !== "all") {
    fetchServiceAreas(editTable.unitCode, setEditServiceAreas);
  } else {
    setEditServiceAreas([]);
  }
}, [editTable.unitCode]);

  return (
    <Box sx={{ p: 4, bgcolor: 'background.default' }}>
      <Typography variant="h4" gutterBottom>
        {itemType} Management
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Select Unit
        </Typography>
        <Tabs value={selectedUnitCode} onChange={handleUnitChange} aria-label="unit selection tabs">
          <Tab label="All Units" value="all" />
          {units.map(unit => (
            <Tab key={unit.unitCode} label={unit.name} value={unit.unitCode} />
          ))}
        </Tabs>
      </Box>

      <Grid container spacing={3}>
        {tables.length === 0 ? (
          <Grid item xs={12}>
            <Typography variant="body1" color="text.secondary" align="center">
              {selectedUnitCode === 'all'
                ? `No ${itemType.toLowerCase()}s in all units.`
                : `No ${itemType.toLowerCase()}s in the selected unit (${units.find(u => u.unitCode === selectedUnitCode)?.name || selectedUnitCode}).`}
            </Typography>
          </Grid>
        ) : (
          tables.map(table => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={table.id}>
              <StyledCard onClick={() => handleOpenEditDialog(table)}>
                <CardContent>
                  <>
                    <Typography variant="h6" align="center">
                      {itemType === 'Room' ? 'Room Number' : 'Table ID'}: {table[idField]}
                    </Typography>
                    {/*<Typography variant="body2" align="center" color="text.secondary">
                      Server: {servers.find(s => s.userId === table.assignedServer)?.name || 'None'}
                    </Typography>*/}
                    {/*<Typography variant="body2" align="center" color="text.secondary">
                      Occupied: {table.occupied ? 'Yes' : 'No'}
                    </Typography>*/}
                    <Typography variant="body2" align="center" color="text.secondary">
  Status: {table.tableStatus || "Available"}
</Typography>
                    <Typography variant="body2" align="center" color="text.secondary">
                      Unit: {table.unitCode || 'N/A'}
                    </Typography>
                  </>
                </CardContent>
              </StyledCard>
            </Grid>
          ))
        )}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <AddCard onClick={handleOpenAddDialog}>
            <CardContent>
              <AddIcon fontSize="large" />
            </CardContent>
          </AddCard>
        </Grid>
      </Grid>

      <Dialog open={openAddDialog} onClose={handleCloseAddDialog}>
        <DialogTitle>Add New {itemType}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="id"
            label={`${itemType} ID`}
            type="text"
            fullWidth
            value={newTable.id}
            onChange={handleNewTableChange}
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel id="unit-select-label">Unit</InputLabel>
            <Select
              labelId="unit-select-label"
              name="unitCode"
              value={newTable.unitCode}
              label="Unit"
              onChange={handleNewTableChange}
              disabled={selectedUnitCode !== 'all'}
            >
              {units.length > 0 ? (
                units.map(unit => (
                  <MenuItem key={unit.unitCode} value={unit.unitCode}>
                    {unit.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No units available
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
  <InputLabel id="service-area-label">Service Area</InputLabel>
  <Select
    labelId="service-area-label"
    name="serviceArea"
    value={newTable.serviceArea || ''}
    label="Service Area"
    onChange={handleNewTableChange}
    //disabled={serviceAreas.length === 0}
  >
    <MenuItem value="">
      <em>None</em>
    </MenuItem>
    {addServiceAreas.map(area => (
      <MenuItem key={area.id} value={area.id}>
        {area.serviceAreaName}  {/* <-- use serviceAreaName instead of name */}
      </MenuItem>
    ))}
  </Select>
</FormControl>
          {/*<FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel id="assigned-server-label">Assigned Server</InputLabel>
            <Select
              labelId="assigned-server-label"
              name="assignedServer"
              value={newTable.assignedServer}
              label="Assigned Server"
              onChange={handleNewTableChange}
              displayEmpty
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {servers.length > 0 ? (
                servers.map(server => (
                  <MenuItem key={server.userId} value={server.userId}>
                    {server.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No servers available
                </MenuItem>
              )}
            </Select>
          </FormControl>*/}
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleGenerateQrCode(newTable.id)}
            sx={{ mt: 2 }}
            disabled={!newTable.id || !newTable.unitCode}
          >
            Generate QR Code
          </Button>
          {newTable.qrCode && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="subtitle1" gutterBottom>
                QR Code for {itemType} {newTable.id}
              </Typography>
              <QRCodeCanvas 
                id={`qrcode-add-${newTable.id}`}
                ref={addQrCodeRef}
                value={newTable.qrCode} 
                size={150} 
                level="H" 
                includeMargin={true} 
              />
              <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
                {newTable.qrCode}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadQrCode(newTable.id)}
                sx={{ mt: 2 }}
              >
                Download QR Code
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button onClick={handleAddTable} color="primary" disabled={!newTable.id || !newTable.qrCode || !newTable.unitCode}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
        <DialogTitle>Manage {selectedTable?.[idField]}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            name="id"
            label={`${itemType} ID`}
            type="text"
            fullWidth
            value={editTable.id}
            onChange={handleEditTableChange}
            disabled={hasTransactions}
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel id="edit-unit-select-label">Unit</InputLabel>
            <Select
              labelId="edit-unit-select-label"
              name="unitCode"
              value={editTable.unitCode}
              label="Unit"
              onChange={handleEditTableChange}
              //disabled={selectedUnitCode !== 'all'}
              disabled={hasTransactions || selectedUnitCode !== 'all'}
            >
              {units.length > 0 ? (
                units.map(unit => (
                  <MenuItem key={unit.unitCode} value={unit.unitCode}>
                    {unit.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No units available
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
  <InputLabel id="edit-service-area-label">Service Area</InputLabel>
  <Select
    labelId="edit-service-area-label"
    name="serviceArea"
    value={editTable.serviceArea || ''}
    label="Service Area"
    onChange={handleEditTableChange}
    //disabled={editServiceAreas.length === 0}
    disabled={hasTransactions || editServiceAreas.length === 0}
  >
    <MenuItem value="">
      <em>None</em>
    </MenuItem>
   {editServiceAreas.map(area => (
      <MenuItem key={area.id} value={area.id}>
        {area.serviceAreaName}  {/* <-- use serviceAreaName instead of name */}
      </MenuItem>
    ))}
  </Select>
</FormControl>
          {/*<FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel id="edit-assigned-server-label">Assigned Server</InputLabel>
            <Select
              labelId="edit-assigned-server-label"
              name="assignedServer"
              value={editTable.assignedServer}
              label="Assigned Server"
              onChange={handleEditTableChange}
              displayEmpty
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {servers.length > 0 ? (
                servers.map(server => (
                  <MenuItem key={server.userId} value={server.userId}>
                    {server.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No servers available
                </MenuItem>
              )}
            </Select>
          </FormControl>*/}
           <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
  <InputLabel id="table-status-label">Table Status</InputLabel>
  <Select
    labelId="table-status-label"
    name="tableStatus"
    value={editTable.tableStatus}
    label="Table Status"
    onChange={handleEditTableChange}
  >
    <MenuItem value="Available">Available</MenuItem>
     <MenuItem value="Occupied">Occupied</MenuItem>
    <MenuItem value="Reserved">Reserved</MenuItem>
    <MenuItem value="Not-Available">Not Available</MenuItem>
  </Select>
</FormControl>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleGenerateQrCode(editTable.id, true)}
            sx={{ mt: 2 }}
            disabled={!editTable.id || !editTable.unitCode}
          >
            Generate QR Code
          </Button>
          {editTable.qrCode && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="subtitle1" gutterBottom>
                QR Code for {itemType} {editTable.id}
              </Typography>
              <QRCodeCanvas 
                id={`qrcode-edit-${editTable.id}`}
                ref={editQrCodeRef}
                value={editTable.qrCode} 
                size={150} 
                level="H" 
                includeMargin={true} 
              />
              <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
                {editTable.qrCode}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadQrCode(editTable.id, true)}
                sx={{ mt: 2 }}
              >
                Download QR Code
              </Button>
            </Box>
          )}

         
          {/*<TextField
            margin="dense"
            name="occupied"
            label="Occupied"
            fullWidth
            select
            value={editTable.occupied.toString()}
            onChange={handleEditTableChange}
            SelectProps={{ native: true }}
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </TextField>
          <TextField
            margin="dense"
            name="currentOrderId"
            label="Current Order ID"
            type="text"
            fullWidth
            value={editTable.currentOrderId}
            onChange={handleEditTableChange}
          />*/}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteTable} color="error" startIcon={<DeleteIcon />}>
            Delete
          </Button>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleUpdateTable} color="primary" startIcon={<EditIcon />} disabled={!editTable.id || !editTable.qrCode || !editTable.unitCode}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageTable;
